import PropTypes from 'prop-types';

export const APPRAISAL_TAB_VIEW_MODE = {
  DEFAULT: 'default',
  LOOKUP_STYLE: 'lookup-style',
  BACKGROUND_STYLE: 'background-style',
  VDP_EMBEDDED: 'vdp-embedded',
  APPRAISAL_LANDING: 'appraisal-landing',
};

const DEFAULT_VIEW = {
  mode: APPRAISAL_TAB_VIEW_MODE.DEFAULT,
  layout: {
    isMobile: false,
    isFullWidth: false,
    useUniqId: false,
  },
  features: {
    hasAddressToVinDrawer: false,
    isBackgroundStyleTabEnabled: false,
    isEmoSearchEnabled: false,
    profileOfferVin: undefined,
  },
  flow: {
    isSellCar: false,
    isSellInCityState: false,
    isWIDG1125chal1: false,
    isPrivatePartyBtn: false,
  },
  placement: {
    isLookUpStyleView: false,
    isVdpEmbedded: false,
    isAppraisalLanding: false,
    isVinByAddressOverlay: false,
    isAddressTab: false,
  },
};

export const appraisalTabViewPropType = PropTypes.shape({
  mode: PropTypes.oneOf(Object.values(APPRAISAL_TAB_VIEW_MODE)),
  layout: PropTypes.shape({
    isMobile: PropTypes.bool,
    isFullWidth: PropTypes.bool,
    useUniqId: PropTypes.bool,
  }),
  features: PropTypes.shape({
    hasAddressToVinDrawer: PropTypes.bool,
    isBackgroundStyleTabEnabled: PropTypes.bool,
    isEmoSearchEnabled: PropTypes.bool,
    profileOfferVin: PropTypes.string,
  }),
  flow: PropTypes.shape({
    isSellCar: PropTypes.bool,
    isSellInCityState: PropTypes.bool,
    isWIDG1125chal1: PropTypes.bool,
    isPrivatePartyBtn: PropTypes.bool,
  }),
  placement: PropTypes.shape({
    isLookUpStyleView: PropTypes.bool,
    isVdpEmbedded: PropTypes.bool,
    isAppraisalLanding: PropTypes.bool,
    isVinByAddressOverlay: PropTypes.bool,
    isAddressTab: PropTypes.bool,
  }),
});

export function getAppraisalTabViewMode({
  isLookUpStyleView,
  isBackgroundStyleTabEnabled,
  isVdpEmbedded,
  isAppraisalLanding,
} = {}) {
  if (isLookUpStyleView) return APPRAISAL_TAB_VIEW_MODE.LOOKUP_STYLE;
  if (isBackgroundStyleTabEnabled) return APPRAISAL_TAB_VIEW_MODE.BACKGROUND_STYLE;
  if (isVdpEmbedded) return APPRAISAL_TAB_VIEW_MODE.VDP_EMBEDDED;
  if (isAppraisalLanding) return APPRAISAL_TAB_VIEW_MODE.APPRAISAL_LANDING;

  return APPRAISAL_TAB_VIEW_MODE.DEFAULT;
}

export function buildAppraisalTabView({
  isMobile = false,
  isFullWidth = false,
  isLookUpStyleView = false,
  isSellCar = false,
  isSellInCityState = false,
  isWIDG1125chal1 = false,
  isVdpEmbedded = false,
  isProfileOfferVinPrepopulationEnabled = false,
  profileOfferVin = '',
  hasAddressToVinDrawer = false,
  isBackgroundStyleTabEnabled = false,
  isEmoSearchEnabled = false,
  isAppraisalLanding = false,
  isPrivatePartyBtn = false,
  isVinByAddressOverlay = false,
  isAddressTab = false,
} = {}) {
  return {
    mode: getAppraisalTabViewMode({
      isLookUpStyleView,
      isBackgroundStyleTabEnabled,
      isVdpEmbedded,
      isAppraisalLanding,
    }),
    layout: {
      isMobile,
      isFullWidth,
      useUniqId: isLookUpStyleView,
    },
    features: {
      hasAddressToVinDrawer,
      isBackgroundStyleTabEnabled,
      isEmoSearchEnabled,
      profileOfferVin: isProfileOfferVinPrepopulationEnabled ? profileOfferVin : undefined,
    },
    flow: {
      isSellCar,
      isSellInCityState,
      isWIDG1125chal1,
      isPrivatePartyBtn,
    },
    placement: {
      isLookUpStyleView,
      isVdpEmbedded,
      isAppraisalLanding,
      isVinByAddressOverlay,
      isAddressTab,
    },
  };
}

export function getAppraisalTabViewProps(view) {
  const resolvedView = {
    ...DEFAULT_VIEW,
    ...view,
    layout: {
      ...DEFAULT_VIEW.layout,
      ...view?.layout,
    },
    features: {
      ...DEFAULT_VIEW.features,
      ...view?.features,
    },
    flow: {
      ...DEFAULT_VIEW.flow,
      ...view?.flow,
    },
    placement: {
      ...DEFAULT_VIEW.placement,
      ...view?.placement,
    },
  };

  return {
    mode: resolvedView.mode,
    isMobile: resolvedView.layout.isMobile,
    isFullWidth: resolvedView.layout.isFullWidth,
    useUniqId: resolvedView.layout.useUniqId,
    hasAddressToVinDrawer: resolvedView.features.hasAddressToVinDrawer,
    isBackgroundStyleTabEnabled: resolvedView.features.isBackgroundStyleTabEnabled,
    isEmoSearchEnabled: resolvedView.features.isEmoSearchEnabled,
    profileOfferVin: resolvedView.features.profileOfferVin,
    isSellCar: resolvedView.flow.isSellCar,
    isSellInCityState: resolvedView.flow.isSellInCityState,
    isWIDG1125chal1: resolvedView.flow.isWIDG1125chal1,
    isPrivatePartyBtn: resolvedView.flow.isPrivatePartyBtn,
    isLookUpStyleView: resolvedView.placement.isLookUpStyleView,
    isVdpEmbedded: resolvedView.placement.isVdpEmbedded,
    isAppraisalLanding: resolvedView.placement.isAppraisalLanding,
    isVinByAddressOverlay: resolvedView.placement.isVinByAddressOverlay,
    isAddressTab: resolvedView.placement.isAddressTab,
  };
}

export function getResolvedAppraisalTabViewProps({ view, ...legacyProps } = {}) {
  const viewProps = getAppraisalTabViewProps(view);

  return view ? { ...legacyProps, ...viewProps } : { ...viewProps, ...legacyProps };
}
